import * as z from 'zod';

export const RedeemPointsForCouponRequest = z.object({
  rewardId: z
    .string()
    .describe(
      'ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    loyaltyAccountId: z
      .string()
      .describe(
        'ID of the loyalty account of the customer redeeming points. See the Loyalty Accounts API for more information.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const RedeemPointsForCouponResponse = z.object({
  coupon: z
    .object({
      _id: z
        .string()
        .describe('Loyalty coupon ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      accountId: z
        .string()
        .describe(
          'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberIdDeprecated: z
        .string()
        .describe(
          'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      transactionId: z
        .string()
        .describe(
          '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      couponReference: z
        .object({
          couponId: z
            .string()
            .describe('Coupon ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          code: z
            .string()
            .describe(
              'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
            )
            .max(20)
            .optional(),
          name: z
            .string()
            .describe('Name of coupon.')
            .max(80)
            .optional()
            .nullable(),
          specification: z
            .intersection(
              z.object({
                name: z
                  .string()
                  .describe('Name of coupon.')
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'UNKNOWN',
                    'MONEY_OFF_AMOUNT',
                    'PERCENT_OFF_RATE',
                    'FREE_SHIPPING',
                    'FIXED_PRICE_AMOUNT',
                    'BUY_X_GET_Y',
                  ])
                  .optional(),
                limitedToOneItem: z
                  .boolean()
                  .describe(
                    'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
                  )
                  .optional()
                  .nullable(),
                appliesToSubscriptions: z
                  .boolean()
                  .describe('Whether the coupon also applies to subscriptions.')
                  .optional()
                  .nullable(),
                discountedCycleCount: z
                  .number()
                  .int()
                  .describe(
                    'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.intersection(
                z.xor([
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                  }),
                  z.object({
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    moneyOffAmount: z
                      .number()
                      .describe('Fixed price discount.')
                      .min(0.01),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    percentOffRate: z
                      .number()
                      .describe('Discount as a percentage.'),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    freeShipping: z
                      .boolean()
                      .describe(
                        'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                      ),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    fixedPriceAmount: z
                      .number()
                      .describe(
                        'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                      ),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z
                      .object({
                        x: z
                          .number()
                          .int()
                          .describe(
                            'Number of purchased items required to receive free items.'
                          )
                          .optional(),
                        y: z
                          .number()
                          .int()
                          .describe(
                            'Number of items received for free if required number of items were purchased.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                      ),
                  }),
                ]),
                z.xor([
                  z.object({
                    scope: z.never().optional(),
                    minimumSubtotal: z.never().optional(),
                  }),
                  z.object({
                    minimumSubtotal: z.never().optional(),
                    scope: z
                      .object({
                        name: z
                          .string()
                          .describe(
                            'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                          )
                          .optional()
                          .nullable(),
                        entityId: z
                          .string()
                          .describe(
                            'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                          )
                          .optional()
                          .nullable(),
                        namespace: z
                          .string()
                          .describe(
                            'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                          )
                          .optional(),
                      })
                      .describe(
                        'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                      ),
                  }),
                  z.object({
                    scope: z.never().optional(),
                    minimumSubtotal: z
                      .number()
                      .describe(
                        'The coupon is only applicable when the order subtotal is over this amount.'
                      ),
                  }),
                ])
              )
            )
            .describe('The information to use when creating the coupon.')
            .optional(),
          deleted: z
            .boolean()
            .describe('Whether the referenced coupon was deleted.')
            .optional()
            .nullable(),
        })
        .describe(
          'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
        )
        .optional(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'ACTIVE', 'APPLIED', 'FAILED', 'ARCHIVED'])
        .describe(
          'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
        )
        .optional(),
      rewardName: z
        .string()
        .describe('Name of reward that was redeemed to create this coupon.')
        .min(1)
        .max(50)
        .optional(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the loyalty coupon was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the loyalty coupon was last updated.')
        .optional()
        .nullable(),
      refundable: z
        .boolean()
        .describe(
          'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
        )
        .optional()
        .nullable(),
    })
    .describe('Created loyalty coupon.')
    .optional(),
});
export const RedeemCurrentMemberPointsForCouponRequest = z.object({
  rewardId: z
    .string()
    .describe(
      'ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RedeemCurrentMemberPointsForCouponResponse = z.object({
  coupon: z
    .object({
      _id: z
        .string()
        .describe('Loyalty coupon ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      accountId: z
        .string()
        .describe(
          'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberIdDeprecated: z
        .string()
        .describe(
          'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      transactionId: z
        .string()
        .describe(
          '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      couponReference: z
        .object({
          couponId: z
            .string()
            .describe('Coupon ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          code: z
            .string()
            .describe(
              'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
            )
            .max(20)
            .optional(),
          name: z
            .string()
            .describe('Name of coupon.')
            .max(80)
            .optional()
            .nullable(),
          specification: z
            .intersection(
              z.object({
                name: z
                  .string()
                  .describe('Name of coupon.')
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'UNKNOWN',
                    'MONEY_OFF_AMOUNT',
                    'PERCENT_OFF_RATE',
                    'FREE_SHIPPING',
                    'FIXED_PRICE_AMOUNT',
                    'BUY_X_GET_Y',
                  ])
                  .optional(),
                limitedToOneItem: z
                  .boolean()
                  .describe(
                    'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
                  )
                  .optional()
                  .nullable(),
                appliesToSubscriptions: z
                  .boolean()
                  .describe('Whether the coupon also applies to subscriptions.')
                  .optional()
                  .nullable(),
                discountedCycleCount: z
                  .number()
                  .int()
                  .describe(
                    'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
                  )
                  .optional()
                  .nullable(),
              }),
              z.intersection(
                z.xor([
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                  }),
                  z.object({
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    moneyOffAmount: z
                      .number()
                      .describe('Fixed price discount.')
                      .min(0.01),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    percentOffRate: z
                      .number()
                      .describe('Discount as a percentage.'),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    freeShipping: z
                      .boolean()
                      .describe(
                        'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                      ),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    buyXGetY: z.never().optional(),
                    fixedPriceAmount: z
                      .number()
                      .describe(
                        'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                      ),
                  }),
                  z.object({
                    moneyOffAmount: z.never().optional(),
                    percentOffRate: z.never().optional(),
                    freeShipping: z.never().optional(),
                    fixedPriceAmount: z.never().optional(),
                    buyXGetY: z
                      .object({
                        x: z
                          .number()
                          .int()
                          .describe(
                            'Number of purchased items required to receive free items.'
                          )
                          .optional(),
                        y: z
                          .number()
                          .int()
                          .describe(
                            'Number of items received for free if required number of items were purchased.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                      ),
                  }),
                ]),
                z.xor([
                  z.object({
                    scope: z.never().optional(),
                    minimumSubtotal: z.never().optional(),
                  }),
                  z.object({
                    minimumSubtotal: z.never().optional(),
                    scope: z
                      .object({
                        name: z
                          .string()
                          .describe(
                            'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                          )
                          .optional()
                          .nullable(),
                        entityId: z
                          .string()
                          .describe(
                            'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                          )
                          .optional()
                          .nullable(),
                        namespace: z
                          .string()
                          .describe(
                            'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                          )
                          .optional(),
                      })
                      .describe(
                        'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                      ),
                  }),
                  z.object({
                    scope: z.never().optional(),
                    minimumSubtotal: z
                      .number()
                      .describe(
                        'The coupon is only applicable when the order subtotal is over this amount.'
                      ),
                  }),
                ])
              )
            )
            .describe('The information to use when creating the coupon.')
            .optional(),
          deleted: z
            .boolean()
            .describe('Whether the referenced coupon was deleted.')
            .optional()
            .nullable(),
        })
        .describe(
          'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
        )
        .optional(),
      status: z
        .enum(['UNKNOWN', 'PENDING', 'ACTIVE', 'APPLIED', 'FAILED', 'ARCHIVED'])
        .describe(
          'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
        )
        .optional(),
      rewardName: z
        .string()
        .describe('Name of reward that was redeemed to create this coupon.')
        .min(1)
        .max(50)
        .optional(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the loyalty coupon was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the loyalty coupon was last updated.')
        .optional()
        .nullable(),
      refundable: z
        .boolean()
        .describe(
          'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
        )
        .optional()
        .nullable(),
    })
    .describe('Created loyalty coupon.')
    .optional(),
});
export const GetLoyaltyCouponRequest = z.object({
  loyaltyCouponId: z
    .string()
    .describe('ID of the loyalty coupon to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetLoyaltyCouponResponse = z.object({
  _id: z
    .string()
    .describe('Loyalty coupon ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  accountId: z
    .string()
    .describe(
      'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  memberIdDeprecated: z
    .string()
    .describe(
      'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  memberId: z
    .string()
    .describe(
      'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  transactionId: z
    .string()
    .describe(
      '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  couponReference: z
    .object({
      couponId: z
        .string()
        .describe('Coupon ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      code: z
        .string()
        .describe(
          'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
        )
        .max(20)
        .optional(),
      name: z
        .string()
        .describe('Name of coupon.')
        .max(80)
        .optional()
        .nullable(),
      specification: z
        .intersection(
          z.object({
            name: z.string().describe('Name of coupon.').optional().nullable(),
            type: z
              .enum([
                'UNKNOWN',
                'MONEY_OFF_AMOUNT',
                'PERCENT_OFF_RATE',
                'FREE_SHIPPING',
                'FIXED_PRICE_AMOUNT',
                'BUY_X_GET_Y',
              ])
              .optional(),
            limitedToOneItem: z
              .boolean()
              .describe(
                'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
              )
              .optional()
              .nullable(),
            appliesToSubscriptions: z
              .boolean()
              .describe('Whether the coupon also applies to subscriptions.')
              .optional()
              .nullable(),
            discountedCycleCount: z
              .number()
              .int()
              .describe(
                'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
              )
              .optional()
              .nullable(),
          }),
          z.intersection(
            z.xor([
              z.object({
                moneyOffAmount: z.never().optional(),
                percentOffRate: z.never().optional(),
                freeShipping: z.never().optional(),
                fixedPriceAmount: z.never().optional(),
                buyXGetY: z.never().optional(),
              }),
              z.object({
                percentOffRate: z.never().optional(),
                freeShipping: z.never().optional(),
                fixedPriceAmount: z.never().optional(),
                buyXGetY: z.never().optional(),
                moneyOffAmount: z
                  .number()
                  .describe('Fixed price discount.')
                  .min(0.01),
              }),
              z.object({
                moneyOffAmount: z.never().optional(),
                freeShipping: z.never().optional(),
                fixedPriceAmount: z.never().optional(),
                buyXGetY: z.never().optional(),
                percentOffRate: z
                  .number()
                  .describe('Discount as a percentage.'),
              }),
              z.object({
                moneyOffAmount: z.never().optional(),
                percentOffRate: z.never().optional(),
                fixedPriceAmount: z.never().optional(),
                buyXGetY: z.never().optional(),
                freeShipping: z
                  .boolean()
                  .describe(
                    'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                  ),
              }),
              z.object({
                moneyOffAmount: z.never().optional(),
                percentOffRate: z.never().optional(),
                freeShipping: z.never().optional(),
                buyXGetY: z.never().optional(),
                fixedPriceAmount: z
                  .number()
                  .describe(
                    'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                  ),
              }),
              z.object({
                moneyOffAmount: z.never().optional(),
                percentOffRate: z.never().optional(),
                freeShipping: z.never().optional(),
                fixedPriceAmount: z.never().optional(),
                buyXGetY: z
                  .object({
                    x: z
                      .number()
                      .int()
                      .describe(
                        'Number of purchased items required to receive free items.'
                      )
                      .optional(),
                    y: z
                      .number()
                      .int()
                      .describe(
                        'Number of items received for free if required number of items were purchased.'
                      )
                      .optional(),
                  })
                  .describe(
                    'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                  ),
              }),
            ]),
            z.xor([
              z.object({
                scope: z.never().optional(),
                minimumSubtotal: z.never().optional(),
              }),
              z.object({
                minimumSubtotal: z.never().optional(),
                scope: z
                  .object({
                    name: z
                      .string()
                      .describe(
                        'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                      )
                      .optional()
                      .nullable(),
                    entityId: z
                      .string()
                      .describe(
                        'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                      )
                      .optional()
                      .nullable(),
                    namespace: z
                      .string()
                      .describe(
                        'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                      )
                      .optional(),
                  })
                  .describe(
                    'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                  ),
              }),
              z.object({
                scope: z.never().optional(),
                minimumSubtotal: z
                  .number()
                  .describe(
                    'The coupon is only applicable when the order subtotal is over this amount.'
                  ),
              }),
            ])
          )
        )
        .describe('The information to use when creating the coupon.')
        .optional(),
      deleted: z
        .boolean()
        .describe('Whether the referenced coupon was deleted.')
        .optional()
        .nullable(),
    })
    .describe(
      'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
    )
    .optional(),
  status: z
    .enum(['UNKNOWN', 'PENDING', 'ACTIVE', 'APPLIED', 'FAILED', 'ARCHIVED'])
    .describe(
      'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
    )
    .optional(),
  rewardName: z
    .string()
    .describe('Name of reward that was redeemed to create this coupon.')
    .min(1)
    .max(50)
    .optional(),
  revision: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the loyalty coupon was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the loyalty coupon was last updated.')
    .optional()
    .nullable(),
  refundable: z
    .boolean()
    .describe(
      'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
    )
    .optional()
    .nullable(),
});
export const BulkGetLoyaltyCouponRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(5)
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(100)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
                ),
            }),
          ])
        )
        .describe('Query to filter loyalty coupons.')
        .optional(),
    })
    .optional(),
});
export const BulkGetLoyaltyCouponResponse = z.object({
  couponsInSite: z
    .array(
      z.object({
        metaSiteId: z
          .string()
          .describe('Metasite ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        loyaltyCoupons: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Loyalty coupon ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              accountId: z
                .string()
                .describe(
                  'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              memberIdDeprecated: z
                .string()
                .describe(
                  'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              memberId: z
                .string()
                .describe(
                  'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              transactionId: z
                .string()
                .describe(
                  '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              couponReference: z
                .object({
                  couponId: z
                    .string()
                    .describe('Coupon ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  code: z
                    .string()
                    .describe(
                      'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
                    )
                    .max(20)
                    .optional(),
                  name: z
                    .string()
                    .describe('Name of coupon.')
                    .max(80)
                    .optional()
                    .nullable(),
                  specification: z
                    .intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Name of coupon.')
                          .optional()
                          .nullable(),
                        type: z
                          .enum([
                            'UNKNOWN',
                            'MONEY_OFF_AMOUNT',
                            'PERCENT_OFF_RATE',
                            'FREE_SHIPPING',
                            'FIXED_PRICE_AMOUNT',
                            'BUY_X_GET_Y',
                          ])
                          .optional(),
                        limitedToOneItem: z
                          .boolean()
                          .describe(
                            'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
                          )
                          .optional()
                          .nullable(),
                        appliesToSubscriptions: z
                          .boolean()
                          .describe(
                            'Whether the coupon also applies to subscriptions.'
                          )
                          .optional()
                          .nullable(),
                        discountedCycleCount: z
                          .number()
                          .int()
                          .describe(
                            'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            moneyOffAmount: z.never().optional(),
                            percentOffRate: z.never().optional(),
                            freeShipping: z.never().optional(),
                            fixedPriceAmount: z.never().optional(),
                            buyXGetY: z.never().optional(),
                          }),
                          z.object({
                            percentOffRate: z.never().optional(),
                            freeShipping: z.never().optional(),
                            fixedPriceAmount: z.never().optional(),
                            buyXGetY: z.never().optional(),
                            moneyOffAmount: z
                              .number()
                              .describe('Fixed price discount.')
                              .min(0.01),
                          }),
                          z.object({
                            moneyOffAmount: z.never().optional(),
                            freeShipping: z.never().optional(),
                            fixedPriceAmount: z.never().optional(),
                            buyXGetY: z.never().optional(),
                            percentOffRate: z
                              .number()
                              .describe('Discount as a percentage.'),
                          }),
                          z.object({
                            moneyOffAmount: z.never().optional(),
                            percentOffRate: z.never().optional(),
                            fixedPriceAmount: z.never().optional(),
                            buyXGetY: z.never().optional(),
                            freeShipping: z
                              .boolean()
                              .describe(
                                'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                              ),
                          }),
                          z.object({
                            moneyOffAmount: z.never().optional(),
                            percentOffRate: z.never().optional(),
                            freeShipping: z.never().optional(),
                            buyXGetY: z.never().optional(),
                            fixedPriceAmount: z
                              .number()
                              .describe(
                                'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                              ),
                          }),
                          z.object({
                            moneyOffAmount: z.never().optional(),
                            percentOffRate: z.never().optional(),
                            freeShipping: z.never().optional(),
                            fixedPriceAmount: z.never().optional(),
                            buyXGetY: z
                              .object({
                                x: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of purchased items required to receive free items.'
                                  )
                                  .optional(),
                                y: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of items received for free if required number of items were purchased.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                              ),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            scope: z.never().optional(),
                            minimumSubtotal: z.never().optional(),
                          }),
                          z.object({
                            minimumSubtotal: z.never().optional(),
                            scope: z
                              .object({
                                name: z
                                  .string()
                                  .describe(
                                    'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                                  )
                                  .optional()
                                  .nullable(),
                                entityId: z
                                  .string()
                                  .describe(
                                    'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                                  )
                                  .optional()
                                  .nullable(),
                                namespace: z
                                  .string()
                                  .describe(
                                    'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                              ),
                          }),
                          z.object({
                            scope: z.never().optional(),
                            minimumSubtotal: z
                              .number()
                              .describe(
                                'The coupon is only applicable when the order subtotal is over this amount.'
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'The information to use when creating the coupon.'
                    )
                    .optional(),
                  deleted: z
                    .boolean()
                    .describe('Whether the referenced coupon was deleted.')
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
                )
                .optional(),
              status: z
                .enum([
                  'UNKNOWN',
                  'PENDING',
                  'ACTIVE',
                  'APPLIED',
                  'FAILED',
                  'ARCHIVED',
                ])
                .describe(
                  'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
                )
                .optional(),
              rewardName: z
                .string()
                .describe(
                  'Name of reward that was redeemed to create this coupon.'
                )
                .min(1)
                .max(50)
                .optional(),
              revision: z
                .string()
                .regex(/^\d+$/, 'Must be a valid UInt64 string')
                .describe(
                  'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the loyalty coupon was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the loyalty coupon was last updated.')
                .optional()
                .nullable(),
              refundable: z
                .boolean()
                .describe(
                  'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
                )
                .optional()
                .nullable(),
            })
          )
          .max(100)
          .optional(),
      })
    )
    .max(100)
    .optional(),
});
export const GetCurrentMemberCouponsRequest = z.object({});
export const GetCurrentMemberCouponsResponse = z.object({
  loyaltyCoupons: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Loyalty coupon ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        accountId: z
          .string()
          .describe(
            'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberIdDeprecated: z
          .string()
          .describe(
            'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe(
            'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        transactionId: z
          .string()
          .describe(
            '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        couponReference: z
          .object({
            couponId: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            code: z
              .string()
              .describe(
                'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
              )
              .max(20)
              .optional(),
            name: z
              .string()
              .describe('Name of coupon.')
              .max(80)
              .optional()
              .nullable(),
            specification: z
              .intersection(
                z.object({
                  name: z
                    .string()
                    .describe('Name of coupon.')
                    .optional()
                    .nullable(),
                  type: z
                    .enum([
                      'UNKNOWN',
                      'MONEY_OFF_AMOUNT',
                      'PERCENT_OFF_RATE',
                      'FREE_SHIPPING',
                      'FIXED_PRICE_AMOUNT',
                      'BUY_X_GET_Y',
                    ])
                    .optional(),
                  limitedToOneItem: z
                    .boolean()
                    .describe(
                      'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
                    )
                    .optional()
                    .nullable(),
                  appliesToSubscriptions: z
                    .boolean()
                    .describe(
                      'Whether the coupon also applies to subscriptions.'
                    )
                    .optional()
                    .nullable(),
                  discountedCycleCount: z
                    .number()
                    .int()
                    .describe(
                      'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
                    )
                    .optional()
                    .nullable(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                    }),
                    z.object({
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      moneyOffAmount: z
                        .number()
                        .describe('Fixed price discount.')
                        .min(0.01),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      percentOffRate: z
                        .number()
                        .describe('Discount as a percentage.'),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      freeShipping: z
                        .boolean()
                        .describe(
                          'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                        ),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      fixedPriceAmount: z
                        .number()
                        .describe(
                          'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                        ),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z
                        .object({
                          x: z
                            .number()
                            .int()
                            .describe(
                              'Number of purchased items required to receive free items.'
                            )
                            .optional(),
                          y: z
                            .number()
                            .int()
                            .describe(
                              'Number of items received for free if required number of items were purchased.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                        ),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      scope: z.never().optional(),
                      minimumSubtotal: z.never().optional(),
                    }),
                    z.object({
                      minimumSubtotal: z.never().optional(),
                      scope: z
                        .object({
                          name: z
                            .string()
                            .describe(
                              'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                            )
                            .optional()
                            .nullable(),
                          entityId: z
                            .string()
                            .describe(
                              'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                            )
                            .optional()
                            .nullable(),
                          namespace: z
                            .string()
                            .describe(
                              'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                            )
                            .optional(),
                        })
                        .describe(
                          'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                        ),
                    }),
                    z.object({
                      scope: z.never().optional(),
                      minimumSubtotal: z
                        .number()
                        .describe(
                          'The coupon is only applicable when the order subtotal is over this amount.'
                        ),
                    }),
                  ])
                )
              )
              .describe('The information to use when creating the coupon.')
              .optional(),
            deleted: z
              .boolean()
              .describe('Whether the referenced coupon was deleted.')
              .optional()
              .nullable(),
          })
          .describe(
            'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PENDING',
            'ACTIVE',
            'APPLIED',
            'FAILED',
            'ARCHIVED',
          ])
          .describe(
            'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
          )
          .optional(),
        rewardName: z
          .string()
          .describe('Name of reward that was redeemed to create this coupon.')
          .min(1)
          .max(50)
          .optional(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the loyalty coupon was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the loyalty coupon was last updated.')
          .optional()
          .nullable(),
        refundable: z
          .boolean()
          .describe(
            'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const QueryLoyaltyCouponsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          accountId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          memberId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          transactionId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          couponReference: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          rewardName: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'accountId',
                'memberId',
                'transactionId',
                'couponReference',
                'status',
                'rewardName',
                '_createdDate',
                '_updatedDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryLoyaltyCouponsResponse = z.object({
  loyaltyCoupons: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Loyalty coupon ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        accountId: z
          .string()
          .describe(
            'Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberIdDeprecated: z
          .string()
          .describe(
            'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe(
            'Member ID of the customer that redeemed points for a coupon. See the Members API for more information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        transactionId: z
          .string()
          .describe(
            '[Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        couponReference: z
          .object({
            couponId: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            code: z
              .string()
              .describe(
                'Coupon code.\n\nUnique code entered by a customer to apply the coupon.'
              )
              .max(20)
              .optional(),
            name: z
              .string()
              .describe('Name of coupon.')
              .max(80)
              .optional()
              .nullable(),
            specification: z
              .intersection(
                z.object({
                  name: z
                    .string()
                    .describe('Name of coupon.')
                    .optional()
                    .nullable(),
                  type: z
                    .enum([
                      'UNKNOWN',
                      'MONEY_OFF_AMOUNT',
                      'PERCENT_OFF_RATE',
                      'FREE_SHIPPING',
                      'FIXED_PRICE_AMOUNT',
                      'BUY_X_GET_Y',
                    ])
                    .optional(),
                  limitedToOneItem: z
                    .boolean()
                    .describe(
                      'Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items\nthat the coupon applies to, only the lowest priced item is discounted.\nCoupons with a `bookings` `namespace` are always limited to 1 item.'
                    )
                    .optional()
                    .nullable(),
                  appliesToSubscriptions: z
                    .boolean()
                    .describe(
                      'Whether the coupon also applies to subscriptions.'
                    )
                    .optional()
                    .nullable(),
                  discountedCycleCount: z
                    .number()
                    .int()
                    .describe(
                      'Specifies the amount of cycles to apply the discount to for a subscription item.\n\nCan only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.\nIf `discountedCycleCount` is empty, the coupon applies to all available cycles.\n\nMin: `1`\n\nMax: `999`'
                    )
                    .optional()
                    .nullable(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                    }),
                    z.object({
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      moneyOffAmount: z
                        .number()
                        .describe('Fixed price discount.')
                        .min(0.01),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      percentOffRate: z
                        .number()
                        .describe('Discount as a percentage.'),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      freeShipping: z
                        .boolean()
                        .describe(
                          'Free shipping. If true, the coupon applies to all items in all `namespaces` on a site.'
                        ),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      buyXGetY: z.never().optional(),
                      fixedPriceAmount: z
                        .number()
                        .describe(
                          'Specific sale price. Currently only supported for coupons with a `stores` `namespace`.'
                        ),
                    }),
                    z.object({
                      moneyOffAmount: z.never().optional(),
                      percentOffRate: z.never().optional(),
                      freeShipping: z.never().optional(),
                      fixedPriceAmount: z.never().optional(),
                      buyXGetY: z
                        .object({
                          x: z
                            .number()
                            .int()
                            .describe(
                              'Number of purchased items required to receive free items.'
                            )
                            .optional(),
                          y: z
                            .number()
                            .int()
                            .describe(
                              'Number of items received for free if required number of items were purchased.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the\nfollowing scenario: if a visitor purchases x number of products, they receive y number of products for free. C\nurrently only supported for coupons with a `stores` `namespace`.'
                        ),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      scope: z.never().optional(),
                      minimumSubtotal: z.never().optional(),
                    }),
                    z.object({
                      minimumSubtotal: z.never().optional(),
                      scope: z
                        .object({
                          name: z
                            .string()
                            .describe(
                              'Group within a `namespace` for which the coupon is applicable.\n\nIf no group is specified, the coupon applies to all items in the namespace.\n`group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported groups for each namespace.'
                            )
                            .optional()
                            .nullable(),
                          entityId: z
                            .string()
                            .describe(
                              'ID of the specific entity in the group for which the coupon is applicable.\n\nIf no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,\nan `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)\nfor a list of currently supported entities for each namespace and group.'
                            )
                            .optional()
                            .nullable(),
                          namespace: z
                            .string()
                            .describe(
                              'Wix application for which the coupon is applicable.\n\nOne of the following:\n+ `"stores"`\n+ `"bookings"`\n+ `"events"`\n+ `"pricingPlans"`'
                            )
                            .optional(),
                        })
                        .describe(
                          'Scope of the coupon. When no scope is defined, the coupon applies to all\nitems in all `namespaces` in the site.'
                        ),
                    }),
                    z.object({
                      scope: z.never().optional(),
                      minimumSubtotal: z
                        .number()
                        .describe(
                          'The coupon is only applicable when the order subtotal is over this amount.'
                        ),
                    }),
                  ])
                )
              )
              .describe('The information to use when creating the coupon.')
              .optional(),
            deleted: z
              .boolean()
              .describe('Whether the referenced coupon was deleted.')
              .optional()
              .nullable(),
          })
          .describe(
            'Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)\nthat is created along with the loyalty coupon.'
          )
          .optional(),
        status: z
          .enum([
            'UNKNOWN',
            'PENDING',
            'ACTIVE',
            'APPLIED',
            'FAILED',
            'ARCHIVED',
          ])
          .describe(
            'Loyalty coupon status.\n\nThis status relates to the corresponding coupon that is created\nat the same time as the loyalty coupon and is included in `couponReference`.'
          )
          .optional(),
        rewardName: z
          .string()
          .describe('Name of reward that was redeemed to create this coupon.')
          .min(1)
          .max(50)
          .optional(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the loyalty coupon was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the loyalty coupon was last updated.')
          .optional()
          .nullable(),
        refundable: z
          .boolean()
          .describe(
            'Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Metadata.')
    .optional(),
});
export const DeleteLoyaltyCouponRequest = z.object({
  _id: z
    .string()
    .describe('ID of the loyalty coupon to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  revision: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      'Revision number, which increments by 1 each time the loyalty coupon is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.'
    ),
});
export const DeleteLoyaltyCouponResponse = z.object({});
